import React from 'react';

import {useConnectedWallet} from '@/utils/wallet/hooks';
import FadeIn from '@/components/FadeIn';

const X = ({className}: {className: string}) => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M6 18L18 6M6 6l12 12"
      />
    </svg>
  );
};

const MintCta = ({
  isSaleOpen = false,
  isSoldOut = false,
}: {
  isSaleOpen: boolean;
  isSoldOut: boolean;
}) => {
  const {connectedAddress} = useConnectedWallet();

  if (isSoldOut) {
    return (
      <div
        className="py-4"
        style={{
          background:
            'linear-gradient(90deg, rgba(167, 136, 79, 0) 0%, #A7884F 50.25%, rgba(167, 136, 79, 0) 100%)',
        }}
      >
        <span className="text-sm font-medium uppercase tracking-widest">
          Minting has ended
        </span>
      </div>
    );
  } else if (isSaleOpen) {
    if (connectedAddress) {
      return (
        <div
          className="py-4"
          style={{
            background:
              'linear-gradient(90deg, rgba(167, 136, 79, 0) 0%, #A7884F 50.25%, rgba(167, 136, 79, 0) 100%)',
          }}
        >
          <span className="text-sm font-medium uppercase tracking-widest">
            Minting is live
          </span>
        </div>
      );
    } else {
      return (
        <button
          className="rounded-lg border border-gold-500 bg-gold-500 py-3 px-6 text-base font-medium text-stone-50 transition-colors hover:text-white sm:py-4 sm:px-8"
          onClick={() => {}}
        >
          Connect your wallet to mint
        </button>
      );
    }
  }

  return (
    <div
      className="py-4"
      style={{
        background:
          'linear-gradient(90deg, rgba(167, 136, 79, 0) 0%, #A7884F 50.25%, rgba(167, 136, 79, 0) 100%)',
      }}
    >
      <span className="text-sm font-medium uppercase tracking-widest">
        Mint begins: May 2, 2022 - 09:00AM PST
      </span>
    </div>
  );
};

export const Hero = ({
  className,
  isSaleOpen = false,
  isSoldOut = false,
}: {
  className?: string;
  isSaleOpen?: boolean;
  isSoldOut?: boolean;
}) => {
  return (
    <div className={`${className} text-center`}>
      <h2 className="mb-4 w-full text-6xl font-medium leading-none tracking-tighter sm:px-4 sm:text-8xl md:text-9xl">
        Cannes
        <br />
        Film Festival
      </h2>

      <div className="mb-6 inline-flex w-full items-center justify-center px-4 sm:mb-8">
        <div className="mx-4 mt-2 h-px flex-1 bg-gradient-to-l from-gold-500"></div>
        <h3 className="inline-flex justify-center font-cormorant text-4xl font-medium italic leading-none text-gold-500 sm:text-5xl">
          <span className="-translate-y-2">75</span>
          <span className="mr-2 text-lg">th</span> Edition
        </h3>
        <div className="mx-4 mt-2 h-px flex-1 bg-gradient-to-r from-gold-500"></div>
      </div>

      <div className="mx-auto max-w-2xl text-center sm:px-4">
        <p className="mb-10 font-medium leading-relaxed text-gray-300 sm:mb-12">
          <a
            className="text-white underline"
            href="https://www.brut.media/us"
            target="_blank"
            rel="noopener noreferrer"
          >
            Brut
          </a>
          , the world’s largest values-driven media platform and official media
          partner of the{' '}
          <a
            className="text-white underline"
            href="https://www.festival-cannes.com/en/"
            target="_blank"
            rel="noopener noreferrer"
          >
            Cannes Film Festival
          </a>
          , has partnered with globally-celebrated Web3 artist,{' '}
          <a
            className="text-white underline"
            href="https://twitter.com/pplpleasr1"
            target="_blank"
            rel="noopener noreferrer"
          >
            pplpleasr
          </a>
          , for a first-of-its-kind collection of 75 Producer Pass NFTs to
          benefit the{' '}
          <a
            className="text-white underline"
            href="https://annenberg.usc.edu/research/aii"
            target="_blank"
            rel="noopener noreferrer"
          >
            USC Annenberg Inclusion Initiative
          </a>{' '}
          and efforts to make the film industry more inclusive to young female
          creators. Owners of the NFT will be invited to celebrate on the red
          carpet at Cannes Film Festival 2022.
        </p>

        <div className="mb-12 sm:mb-16">
          {/* TODO: figure out a better design for this component */}
          <FadeIn delay={200}>
            <div className="md:px-32">
              <MintCta isSaleOpen={isSaleOpen} isSoldOut={isSoldOut} />
            </div>
          </FadeIn>
        </div>

        <p className="mb-6 text-xs font-medium uppercase tracking-widest">
          Made possible by
        </p>

        <div className="flex items-center justify-center space-x-8">
          <div className="flex space-x-2 border border-stone-500 px-2">
            <div className="flex items-center justify-center sm:w-24">
              <a
                href="https://www.brut.media/us"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img
                  alt="Brut logo"
                  src="/images/brut-logo.svg"
                  className="h-6 sm:h-8"
                />
              </a>
            </div>

            <div className="mt-2 flex items-center justify-center sm:w-24">
              <a
                href="https://www.festival-cannes.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="relative top-1"
              >
                <img
                  alt="Cannes logo"
                  src="/images/cannes-updated-logo.png"
                  className="h-16 sm:h-20"
                />
              </a>
            </div>
          </div>

          <X className="relative h-5 w-5 text-gold-500 opacity-80 sm:left-2" />

          <div className="flex items-center justify-center sm:w-24">
            <a
              href="https://shibuya.film"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img
                alt="Shibuya logo"
                src="/images/shibuya-logo-white.svg"
                className="h-10 cursor-pointer sm:h-12"
              />
            </a>
          </div>
        </div>

        <div className="mt-2 flex items-center justify-center">
          <a
            href="https://annenberg.usc.edu/research/aii"
            target="_blank"
            rel="noopener noreferrer"
          >
            <img
              alt="USC logo"
              src="/images/usc-logo.png"
              className="h-16 sm:h-20"
            />
          </a>
        </div>
      </div>
    </div>
  );
};

export default Hero;
